/**
 * Hook da posicao aberta MtM por mes pros proximos 6 meses (Task #73).
 *
 * Mesmo pattern do usePosicaoAberta: cache module-level + refetch periodico
 * (TTL 10min — atualiza junto com curva forward). Estado expoe data + loading
 * + error pra UI consumir e decidir freshness/badge sem prop drilling.
 *
 * Em caso de erro de rede/auth/5xx → fallback EMPTY com last_sync=null
 * (UI renderiza badge VERMELHA "DADO INDISPONIVEL", consistente com cron
 * parado).
 */
import { useEffect, useState } from "react";
import { getPosicaoAbertaMtm6m } from "../api/client";
import type { PosicaoAbertaMtm6mResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PosicaoAbertaMtm6mResponse>;
}

// Task #207: cache chaveado por mes_inicio ("default" quando undefined) — a
// Tabela 6m agora ancora no caixa do proximo 6du, entao a janela varia com o
// calendario; um cache unico vazaria meses de uma ancora pra outra.
const cache = new Map<string, CacheEntry>();

export function invalidatePosicaoAbertaMtm6mCache(): void {
  cache.clear();
}

const EMPTY: PosicaoAbertaMtm6mResponse = {
  meses: [],
  posicao_aberta_last_sync: null,
};

export interface UsePosicaoAbertaMtm6mState {
  data: PosicaoAbertaMtm6mResponse;
  isLoading: boolean;
  error: Error | null;
}

export function usePosicaoAbertaMtm6m(mesInicio?: string): UsePosicaoAbertaMtm6mState {
  const [state, setState] = useState<UsePosicaoAbertaMtm6mState>({
    data: EMPTY,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    // mesInicio undefined -> "default" (backend usa _proximo_mes; legado).
    const key = mesInicio ?? "default";

    function load() {
      const now = Date.now();
      const entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        cache.set(key, {
          expiresAt: now + REFETCH_MS,
          promise: getPosicaoAbertaMtm6m(mesInicio ? { mes_inicio: mesInicio } : undefined),
        });
      }
      cache
        .get(key)!
        .promise.then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          setState({ data: EMPTY, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mesInicio]);

  return state;
}
